// Back in Stock notify component.
// Rendered by the product "Back in Stock" block on sold-out variants. Opens a
// modal (<dialog> in the top layer) with an email field that subscribes the
// shopper to Klaviyo's back-in-stock flow via the client API.
//
// Listeners are bound ONCE in the constructor and delegated from the host
// element (stable), and child nodes are queried fresh at event time, so the
// component keeps working even if the theme re-renders the dialog after connect.
//
// The block is rendered for every variant and toggled through `data-active`,
// because the theme does NOT re-render it on a variant switch. It patches the
// buy buttons in place and leaves everything else alone, so this component has
// to follow the theme's `variant:change` event on the product form itself.
(function () {
  if (customElements.get('back-in-stock')) return;

  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const KLAVIYO_REVISION = '2026-07-15';

  // Mirrors --bis-transition in back-in-stock.css. The dialog stays open until the exit
  // animation has run, so the panel is not ripped out of the top layer mid-transition.
  const BIS_TRANSITION_MS = 220;

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class BackInStock extends HTMLElement {
    constructor() {
      super();
      this._closeTimer = null;
      this._cursorTimer = null;
      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onInput = this._onInput.bind(this);
      this._onCancel = this._onCancel.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
      this._productForm = null;
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
      this.addEventListener('input', this._onInput);
      // Escape triggers `cancel` first; intercept it so the exit animation can play.
      this.addEventListener('cancel', this._onCancel);
    }

    connectedCallback() {
      // The theme dispatches `variant:change` on the product form (see theme.js.liquid), which is
      // an ANCESTOR of this element, so the event never bubbles through here and has to be bound
      // on the form itself. A stable bound reference is kept so it can be removed again.
      this._productForm = document.forms[this.dataset.formId] || null;
      if (this._productForm) {
        this._productForm.addEventListener('variant:change', this._onVariantChange);
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this._closeTimer);
      window.clearTimeout(this._cursorTimer);
      if (this._productForm) {
        this._productForm.removeEventListener('variant:change', this._onVariantChange);
        this._productForm = null;
      }
    }

    get _dialog() { return this.querySelector('.back-in-stock__dialog'); }
    get _input() { return this.querySelector('.back-in-stock__input'); }
    get _status() { return this.querySelector('.back-in-stock__status'); }
    get _statusText() { return this.querySelector('.back-in-stock__status-text'); }
    get _submitButton() { return this.querySelector('.back-in-stock__submit'); }
    get _doneButton() { return this.querySelector('.back-in-stock__done'); }

    _onClick(event) {
      if (event.target.closest('.back-in-stock__trigger')) this._openDialog();
      else if (event.target.closest('.back-in-stock__close')) this._closeDialog();
      else if (event.target.closest('.back-in-stock__done')) this._closeDialog();
      else if (event.target.closest('.back-in-stock__submit')) { event.preventDefault(); this._submit(); }
      // The dialog is stretched full-bleed behind the panel, so a click landing on the
      // dialog itself means the shopper clicked outside the card.
      else if (event.target.classList.contains('back-in-stock__dialog')) this._closeDialog();
    }

    _onKeydown(event) {
      // Focus trap and focus restore are handled natively by <dialog>.
      if (event.key === 'Enter' && event.target === this._input) {
        event.preventDefault();
        this._submit();
      }
    }

    _onCancel(event) {
      event.preventDefault();
      this._closeDialog();
    }

    _onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      const previewMode = this.dataset.previewMode === 'true';
      // A null variant means the chosen option combination does not exist, so there is nothing
      // to subscribe to and the theme shows its own "unavailable" state.
      const shouldShow = previewMode || !!(variant && variant.available === false);

      if (variant && variant.id != null) {
        if (String(variant.id) !== this.dataset.variantId) this._resetForNewVariant();
        this.dataset.variantId = String(variant.id);
        this._updateProductContext(variant);
      }

      this._setActive(shouldShow);
    }

    /** Shows or hides the notify button; the buy buttons follow via the [data-active] CSS rule. */
    _setActive(active) {
      const wasActive = this.dataset.active === 'true';
      if (active === wasActive) return;
      // Hiding the host while the modal is open would strand an open <dialog> in the top layer,
      // leaving the page inert. Close it outright, skipping the exit animation.
      if (!active) this._closeDialog({ immediate: true });
      this.dataset.active = active ? 'true' : 'false';
    }

    /** A different variant is a different subscription, so any previous outcome is discarded. */
    _resetForNewVariant() {
      this.classList.remove('back-in-stock--submitted');
      this._setStatus('', '');
      this._setBusy(false);
    }

    _updateProductContext(variant) {
      const variantLine = this.querySelector('.back-in-stock__product-variant');
      if (variantLine && variant.title) variantLine.textContent = variant.title;

      const thumb = this.querySelector('.back-in-stock__thumb');
      const source = variant.featured_media
        && variant.featured_media.preview_image
        && variant.featured_media.preview_image.src;
      if (!thumb || !source) return;
      // The server-rendered responsive attributes describe the previous image, so drop them
      // before pointing the tile at the new one.
      thumb.removeAttribute('srcset');
      thumb.removeAttribute('sizes');
      thumb.src = source + (source.indexOf('?') === -1 ? '?' : '&') + 'width=160';
    }

    _onInput(event) {
      // Clear a shown error as soon as the shopper edits the field.
      if (event.target === this._input) this._setStatus('', '');
    }

    _openDialog() {
      const dialog = this._dialog;
      if (!dialog) return;

      window.clearTimeout(this._closeTimer);
      window.clearTimeout(this._cursorTimer);

      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }

      // Force a style/layout flush so the transition has a start value, then flip the class in
      // the same task. requestAnimationFrame is deliberately avoided here: it does not fire in
      // throttled or occluded tabs, which would leave the panel stuck at opacity 0 while the
      // dialog still blocks the page.
      void dialog.offsetWidth;
      dialog.classList.add('is-open');

      // Same treatment as the theme's drawers: the close cursor only appears once the modal has
      // finished animating in (see .drawer.show-close-cursor in theme.css).
      const duration = prefersReducedMotion() ? 0 : BIS_TRANSITION_MS;
      this._cursorTimer = window.setTimeout(() => dialog.classList.add('show-close-cursor'), duration);

      // showModal() focuses the first focusable child (the close button); move it to the field
      // the shopper actually came for.
      const focusTarget = this.classList.contains('back-in-stock--submitted')
        ? this._doneButton
        : this._input;
      if (focusTarget) focusTarget.focus();
    }

    /** @param {{immediate?: boolean}} [options] skip the exit animation and close in this task. */
    _closeDialog(options) {
      const dialog = this._dialog;
      if (!dialog) return;

      window.clearTimeout(this._closeTimer);
      window.clearTimeout(this._cursorTimer);
      dialog.classList.remove('show-close-cursor', 'is-open');

      const finish = () => {
        if (typeof dialog.close === 'function' && dialog.open) dialog.close();
        else dialog.removeAttribute('open');
      };

      if ((options && options.immediate) || prefersReducedMotion()) finish();
      else this._closeTimer = window.setTimeout(finish, BIS_TRANSITION_MS);
    }

    _setStatus(message, state) {
      const status = this._status;
      const statusText = this._statusText;
      if (statusText) statusText.textContent = message || '';
      if (status) status.dataset.state = message ? state || '' : '';
    }

    _setBusy(isBusy) {
      const submitButton = this._submitButton;
      if (!submitButton) return;
      submitButton.disabled = isBusy;
      submitButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }

    _isValidEmail(value) {
      // Deliberately permissive: only reject clearly-broken input (no "@", spaces,
      // empty side). Klaviyo is the source of truth, so unusual-but-valid addresses
      // (plus tags, subdomains, long or internationalized/IDN domains) are NOT blocked
      // here. HTML5 type=email validation is too strict for those.
      return /^[^\s@]+@[^\s@]+$/.test(value);
    }

    _sendSubscription(email) {
      const publicKey = this.dataset.publicKey;
      const variantId = this.dataset.variantId;
      return fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(publicKey), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
            }
          }
        })
      });
    }

    async _submit() {
      const input = this._input;
      const email = (input && input.value ? input.value : '').trim();
      if (!this._isValidEmail(email)) {
        this._setStatus(this.dataset.msgInvalid || 'Please enter a valid email address.', 'error');
        if (input) input.focus();
        return;
      }
      if (!this.dataset.publicKey || !this.dataset.variantId) {
        this._setStatus(this.dataset.msgError || 'Something went wrong. Please try again later.', 'error');
        return;
      }

      this._setBusy(true);
      this._setStatus('', '');

      try {
        const response = await this._sendSubscription(email);
        if (response.ok) {
          this._setBusy(false);
          this.classList.add('back-in-stock--submitted');
          this._setStatus(this.dataset.msgSuccess || 'You are on the list. We will email you when it is back.', 'success');
          // The form is gone at this point, so move focus to the remaining exit action.
          const doneButton = this._doneButton;
          if (doneButton) doneButton.focus();
        } else {
          this._setBusy(false);
          // 400 from this endpoint almost always means Klaviyo rejected the email itself.
          const message = response.status === 400
            ? (this.dataset.msgInvalid || 'Please enter a valid email address.')
            : (this.dataset.msgError || 'Something went wrong. Please try again later.');
          this._setStatus(message, 'error');
        }
      } catch (error) {
        // fetch rejected: the request never reached the server (offline, CORS, timeout).
        this._setBusy(false);
        this._setStatus(this.dataset.msgNetwork || 'Connection failed. Please check your internet and try again.', 'error');
      }
    }
  }

  customElements.define('back-in-stock', BackInStock);
})();
